import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { playerDataSelector } from '../../../../../common/store/selectors/playerData.selector';
import { VideoData } from '../../../../../common/store/store.types';
import { useStyles } from '../../../../../common/hooks/useStyles';
import { getGalleryVideoStyleObject } from './galleryVideo.style';
import { css } from '../../../../../common/styles/styles.utils';
import { getTimePresentation } from '../../../../../common/utils/utils';
import {
  playAnotherVideoFromList,
  playVideoFromRecommendations,
  setUserInteraction
} from '../../../store/moreVideos.actions';
import { mediaDataSelector } from '../../../../../common/store/selectors/mediaData.selector';
import { useHoverRef } from '../../../../../common/hooks/useHoverRef';
import { getLargeGalleryVideoUnitId } from '../../../htmlElements/htmlElementsIdGetter';

const ARIA_LABEL = 'Play more videos';

interface GalleryVideoVariablesProps {
  videoData: VideoData;
  isFirstVideoOnList: boolean;
  screenView: number;
  numberOfVideoSpaces: number;
  updatedVideoList: Array<VideoData>;
}

export const GalleryVideo: FunctionComponent<GalleryVideoVariablesProps> = props => {
  const { videoData, isFirstVideoOnList, screenView, numberOfVideoSpaces, updatedVideoList } = props;
  const { duration, title, thumbnail, index } = videoData;

  const dispatch = useDispatch();
  const playerSize = useSelector(playerDataSelector.playerSize);
  const videoList = useSelector(mediaDataSelector.videoList);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);

  const isSingleVideo = videoList.length === 1;
  const galleryViewLength = updatedVideoList.length;

  const [galleryVideoRef, galleryVideoHovered] = useHoverRef();

  const styles = useStyles(getGalleryVideoStyleObject, [playerSize, galleryVideoHovered, isFirstVideoOnList, screenView, galleryViewLength, numberOfVideoSpaces]);
  const { galleryVideoContainerStyle, imageStyle, infoWrapperStyle, titleStyle, durationStyle } = styles;

  const onVideoClicked = (): void => {
    dispatch(setUserInteraction('moreVideosUnitSelected'));
    if (isSingleVideo) {
      dispatch(playVideoFromRecommendations(index));
    } else {
      dispatch(playAnotherVideoFromList(index));
    }
  };

  const onVideoKeyDown = (event: any): void => {
    if (event.key === 'Enter') {
      onVideoClicked();
    }
  };

  return (
    <div
      style={css(galleryVideoContainerStyle)}
      ref={galleryVideoRef}
      onClick={onVideoClicked}
      onKeyDown={onVideoKeyDown}
      tabIndex={0}
      aria-label={ARIA_LABEL}
      id={getLargeGalleryVideoUnitId(index, playerUniqueId)}
    >
      <img src={thumbnail} alt="video thumbnail" style={css(imageStyle)} />
      { galleryVideoHovered && (
        <div style={css(infoWrapperStyle)}>
          <span style={titleStyle}>
            {title}
          </span>
          <span style={css(durationStyle)}>
            {getTimePresentation(Math.floor(duration))}
          </span>
        </div>
      )}
    </div>
  );
};
